import { observer } from 'mobx-react';

import {
  DataGrid, DollarColumn, SelectMode,
} from '@roc/ui';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import { rentColumns, salesColumns } from '../utils/gridsColumns';
import { formatAbbreviatedCurrency } from '@roc/feature-comp-tool';
import { GreenArrow, RedArrow } from '@roc/feature-comp-tool';
import { numberWithCommas } from '@roc/feature-utils';
import { useStore } from '@roc/client-portal-shared/hooks';
import { useEffect, useRef, useState } from 'react';
import PropertyViewModal from './propertyViewModal';
import { ReactComponent as HouseWithPlusFilledIcon } from '../images/housewithplusfilled.svg';
import { valueOrDash } from '@roc/feature-valuation-review-v2';

const useStyles = makeStyles(() =>
  createStyles({
    pagination: {
      justifyContent: 'flex-end',
    },
    housePlusIcon: {
      width: "16px",
      height: "16px",
      color: "#1976d2",
      marginRight: 4,
      flexShrink: 0,
    }

  })
);

export const CompsListView = observer(() => {
  const classes = useStyles();
  const { compToolStoreV2: compToolStore } = useStore();
  const { compResponse, subjectProperty, gridApi, compToolFeedbackStore } = compToolStore;
  const [openViewModal, setOpenViewModal] = useState(false);
  const firstRenderDoneRef = useRef(false);
  const [gridKey, setGridKey] = useState(0);//this is used to rerender the grid when changing pages

  const getArrowSymbol = (key, data) => {
    if (subjectProperty.data[key] && data && data[key]) {
      if (subjectProperty.data[key] > data[key]) {
        return <RedArrow />;
      } else if (subjectProperty.data[key] < data[key]) {
        return <GreenArrow />;
      }
    }
    return null;
  }

  const properties = compResponse.properties;

  const mapPropertyToRow = (property) => ({
    property_id: property.property_id,
    ...property.data,
    ...property.similarity,
    number: property.number,
  });

  const manuallyAddedComparableProperties = compToolStore.manuallyAddedComparables;
  const rows = [...manuallyAddedComparableProperties, ...properties].map(mapPropertyToRow);


  const frameworkComponents = {
    addressCellRenderer(params) {
      const address = params.value;
      const propertyId = params?.data?.property_id;

      return (
        <div
          onClick={() => openIndividualPropertyModal(address)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            textDecoration: 'underline',
            color: '#5c94de',
            cursor: 'pointer'
          }}>
          {propertyId && shouldShowManuallyAddedIcon(propertyId) && (
            <HouseWithPlusFilledIcon className={classes.housePlusIcon} />
          )}{address}
        </div>
      );
    },
    moneyAbbreviatedRenderer(params) {
      return (
        <div>
          {formatAbbreviatedCurrency(params.value)} {getArrowSymbol(params.column.colId, params.data)}
        </div>
      );
    },
    rentAbbreviatedRenderer(params) {
      const abbreviatedCurrency = formatAbbreviatedCurrency(
        params.value
      )
      const value = abbreviatedCurrency == 'N/A' ? abbreviatedCurrency : `${abbreviatedCurrency}/mo`
      return (
        <div>
          {value} {getArrowSymbol(params.column.colId, params.data)}
        </div>
      );
    },
    moneyRenderer(params) {
      return DollarColumn(
        params.value
      )
    },
    moneyArrowRenderer(params) {
      const value = params.value != null ? numberWithCommas(params.value) : '-';
      return (
        <div>
          {value} {getArrowSymbol(params.column.colId, params.data)}
        </div>
      );
    },
    distanceCellRenderer(params) {
      return params.value != null
        ? `${params.value.toFixed(2)} mi`
        : '-';
    },
    valueCellRenderer(params) {
      return valueOrDash(params.value);
    },
    valueArrowCellRenderer(params) {
      const value = valueOrDash(params.value);
      return (
        <div>
          {value} {getArrowSymbol(params.column.colId, params.data)}
        </div>
      );

    }
  };

  const rowSelected = e => {
    if (!firstRenderDoneRef.current) return;

    const property_id = e.data.property_id;
    let property = properties.find(p => p.property_id === property_id);
    if (!property) {
      property = manuallyAddedComparableProperties.find(p => p.property_id === property_id);
    }
    compToolStore.addSelectCompareProperties(property);
  }

  const openIndividualPropertyModal = (address) => {
    const property = compToolStore.getCompResponse().properties.find(p => p.data.address === address);
    if (property) {
      compToolStore.setSelectedProperty(property);
      compToolFeedbackStore.reset();
      setOpenViewModal(true);
    } else {
      const manuallyAddedProperties = compToolStore.manuallyAddedComparables.find(p => p.data.address === address);
      compToolStore.setSelectedProperty(manuallyAddedProperties);
      setOpenViewModal(true);
    }

  }


  const shouldShowManuallyAddedIcon = (property_id) => {
    const property = compToolStore.manuallyAddedComparables.find(p => p.property_id === property_id);
    if (property)
      return true;
    return false;
  }

  const gridReady = e => {
    compToolStore.setGridApi(e.api);
  }

  const topRow = [{ ...subjectProperty.data, ...subjectProperty.similarity }];

  const selectRows = () => {
    gridApi.forEachNode((node) => {
      if (compToolStore.selectedCompareProperties?.some(p => p.property_id === node.data.property_id)) {
        node.setSelected(true);
      }
    });
    setTimeout(() => {
      firstRenderDoneRef.current = true;
    }, 500);
  }

  const onFirstDataRendered = () => {
    if (!gridApi) return;
    firstRenderDoneRef.current = false;
    selectRows();
  }
  useEffect(() => {
    setGridKey((prevValue) => prevValue + 1);
  }, [properties, manuallyAddedComparableProperties]);

  return (
    <Box display="flex" gridGap={1}>
      <DataGrid
        key={`comparables-grid-${gridKey}`}
        selectMode={SelectMode.MULTIPLE}
        columns={compToolStore.rentalComps ? rentColumns : salesColumns}
        onRowSelected={e => rowSelected(e)}
        rows={rows}
        rowData={rows}
        rowModelType="clientSide"
        frameworkComponents={frameworkComponents}
        showFilters={false}
        domLayout="autoHeight"
        gridReady={e => gridReady(e)}
        suppressRowClickSelection={true}
        pinnedTopRowData={topRow}
        firstDataRendered={onFirstDataRendered}
        onSortChanged={() => {/** */ }}
      />
      <PropertyViewModal open={openViewModal} property={compToolStore.selectedProperty} onClose={() => setOpenViewModal(false)} />
    </Box>
  )

})